import { useState, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import TextFieldWithActions from "../../Form/TextFieldWithActions";
import ModalBasicLayout from "../ModalBasicLayout";
import Button from "../../Buttons/Button";
import { Coins } from "lucide-react";
import {
  CREDITS,
  FIELD_REQUIRED,
  AD_ACCOUNT_LINKEDIN_PLACEHOLDER,
  LINKEDIN_ARTICLE_DEFAULT_TEXT,
  CREDIT_REWARD_MODAL_TITLE,
  CREDIT_REWARD_MODAL_SUBTITLE,
  CREDIT_REWARD_MODAL_ARIA_LABEL,
  CREDIT_REWARD_MODAL_REWARD,
  CREDIT_REWARD_MODAL_REWARD_DESCRIPTION,
  CREDIT_REWARD_MODAL_ARTICLE_CONTENT_LABEL,
  CREDIT_REWARD_MODAL_VERIFICATION_NOTICE,
  CREDIT_REWARD_MODAL_BUTTON_CANCEL,
  CREDIT_REWARD_MODAL_BUTTON_PUBLISH,
  CREDIT_REWARD_MODAL_BUTTON_PUBLISHING,
  CREDIT_REWARD_MODAL_BUTTON_RETRY,
  CREDIT_REWARD_MODAL_LOADING_TITLE,
  CREDIT_REWARD_MODAL_LOADING_DESCRIPTION,
  CREDIT_REWARD_MODAL_SUCCESS_TITLE,
  CREDIT_REWARD_MODAL_SUCCESS_TEXT,
  DESCRIPTION_INSTAGRAM_GENERATING,
} from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BORDER_RADIUS,
  LINKEDIN,
  CREDITS_REWARD_LINKEDIN_ARTICLE,
} from "../../../utils/constants";
import SelectBusinessAccount from "../../Meta/SelectBusinessAccount";
import { useDispatchApp } from "../../../lib/redux";
import { getNameBusinessTheme } from "../../../actions/getters";
import {
  startPublishLinkedInPartnershipPostNow,
  startRewriteLinkedInPartnershipTextNow,
} from "../../../actions/user";
import { startGetLinkedInPostShared } from "../../../actions/payments";
import { SimpleAlert } from "../../Alerts/Alerts";
import LoadingDots from "../../Gallery/ShareModalMultiple/components/LoadingDots";

const CustomTitle = () => {
  const { t } = useTranslationApp();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        width: "100%",
        pt: 2,
        pl: 3,
      }}
    >
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t(CREDIT_REWARD_MODAL_TITLE)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(CREDIT_REWARD_MODAL_SUBTITLE)}
        </Typography>
      </Box>
    </Box>
  );
};

const LoadingState = () => {
  const { t } = useTranslationApp();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        py: 6,
      }}
    >
      <CircularProgress size={40} sx={{ color: "primary.main" }} />
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t(CREDIT_REWARD_MODAL_LOADING_TITLE)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(CREDIT_REWARD_MODAL_LOADING_DESCRIPTION)}
        </Typography>
      </Box>
    </Box>
  );
};

const FormContent = ({
  control,
  errors,
  text,
  selectedAccount,
  isLoading,
  isGeneratingText,
  hasError,
  onChangeLinkedInAccount,
  onSubmit,
  onClose,
  onGenerateText,
}) => {
  const { t } = useTranslationApp();

  const articleLabel = isGeneratingText ? (
    <Box sx={{ display: "flex", gap: 1 }} component="span">
      {t(DESCRIPTION_INSTAGRAM_GENERATING)}
      <LoadingDots />
    </Box>
  ) : (
    ""
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          bgcolor: "#fafafa",
          borderRadius: BORDER_RADIUS,
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          mt: 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {t(CREDIT_REWARD_MODAL_REWARD)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(CREDIT_REWARD_MODAL_REWARD_DESCRIPTION)}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Coins size={16} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              {CREDITS_REWARD_LINKEDIN_ARTICLE}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              {t(CREDITS)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box>
        <Typography
          variant="body2"
          sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}
        >
          {t(AD_ACCOUNT_LINKEDIN_PLACEHOLDER)}
        </Typography>
        <SelectBusinessAccount
          variant="filled"
          name="account"
          label={t(AD_ACCOUNT_LINKEDIN_PLACEHOLDER)}
          fullWidth={true}
          control={control}
          platform={LINKEDIN}
          errors={errors}
          onChangeAction={onChangeLinkedInAccount}
          linkedInForPublishing={true}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
        />
      </Box>

      <Box>
        <TextFieldWithActions
          label={articleLabel}
          labelTitle={t(CREDIT_REWARD_MODAL_ARTICLE_CONTENT_LABEL)}
          name="text"
          control={control}
          errors={errors}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
          showCounterCharacters
          maxLength={5000}
          fullWidth
          showAddIcon={false}
          showDeleteIcon={false}
          type="descriptions"
          platform="linkedIn"
          onGenerate={onGenerateText}
          disabled={isGeneratingText}
          readOnly={true}
          sx={{}}
        />
        <Typography variant="caption" color="text.secondary">
          {t(CREDIT_REWARD_MODAL_VERIFICATION_NOTICE)}
        </Typography>
      </Box>
      <DialogActions sx={{ px: 0, gap: 2 }}>
        <Button color="secondary" onClick={onClose} sx={{ flex: 1 }}>
          {t(CREDIT_REWARD_MODAL_BUTTON_CANCEL)}
        </Button>
        <Button
          onClick={onSubmit}
          disabled={
            !text?.trim() || !selectedAccount || isLoading || isGeneratingText
          }
          sx={{ flex: 1 }}
        >
          {isLoading
            ? t(CREDIT_REWARD_MODAL_BUTTON_PUBLISHING)
            : hasError
              ? t(CREDIT_REWARD_MODAL_BUTTON_RETRY)
              : t(CREDIT_REWARD_MODAL_BUTTON_PUBLISH)}
        </Button>
      </DialogActions>
    </Box>
  );
};

const CreditRewardModal = ({ modalOpen, onCloseModal, onSubmitPost }) => {
  const { t, i18n } = useTranslationApp();
  const dispatch = useDispatchApp();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isGeneratingText, setIsGeneratingText] = useState(false);
  const {
    control,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      account: null,
      text: "",
    },
  });

  const text = watch("text");
  const language = i18n.language;

  const rewriteArticleText = useCallback(
    async (baseText) => {
      const previousValue = baseText;

      try {
        setIsGeneratingText(true);
        setValue("text", "");

        const rewrittenText = await dispatch(
          startRewriteLinkedInPartnershipTextNow({
            text: baseText,
            language,
          })
        );

        if (rewrittenText) {
          setValue("text", rewrittenText);
        } else {
          setValue("text", previousValue);
        }
      } catch (error) {
        console.error("Error rewriting LinkedIn article text:", error);
        setValue("text", previousValue);
      } finally {
        setIsGeneratingText(false);
      }
    },
    [dispatch, language, setValue]
  );

  const generateInitialText = useCallback(async () => {
    const defaultText = t(LINKEDIN_ARTICLE_DEFAULT_TEXT, { NAME_BUSINESS });
    await rewriteArticleText(defaultText);
  }, [rewriteArticleText, t, NAME_BUSINESS]);

  useEffect(() => {
    if (modalOpen) {
      setHasError(false);
      generateInitialText();
    }
  }, [modalOpen, generateInitialText]);

  const onChangeLinkedInAccount = useCallback(
    (account) => {
      if (account) {
        setSelectedAccount(account);
        setValue("account", account.id);
      }
    },
    [setValue]
  );

  const onGenerateArticleText = useCallback(async () => {
    const currentValue =
      getValues("text") || t(LINKEDIN_ARTICLE_DEFAULT_TEXT, { NAME_BUSINESS });
    await rewriteArticleText(currentValue);
  }, [getValues, rewriteArticleText, t, NAME_BUSINESS]);

  const onSubmit = async () => {
    if (!text?.trim() || !selectedAccount) return;

    setIsLoading(true);
    setHasError(false);

    try {
      const response = await dispatch(
        startPublishLinkedInPartnershipPostNow({
          integrationID: selectedAccount.integrationID,
          adAccountID: selectedAccount.id,
          text: text.trim(),
          publishAs: selectedAccount.publishAs,
        })
      );

      if (response?.ok) {
        setIsLoading(false);

        await dispatch(startGetLinkedInPostShared());

        if (onSubmitPost) {
          await onSubmitPost(text);
        }
        await SimpleAlert({
          title: t(CREDIT_REWARD_MODAL_SUCCESS_TITLE),
          text: t(CREDIT_REWARD_MODAL_SUCCESS_TEXT, {
            credits: CREDITS_REWARD_LINKEDIN_ARTICLE,
          }),
          icon: "success",
        });
        onClose();
      } else {
        setIsLoading(false);
        setHasError(true);
      }
    } catch (error) {
      console.error("Error publishing LinkedIn article:", error);
      setIsLoading(false);
      setHasError(true);
    }
  };

  const onClose = () => {
    reset({
      account: null,
      text: "",
    });
    setSelectedAccount(null);
    setIsLoading(false);
    setHasError(false);
    setIsGeneratingText(false);
    onCloseModal();
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onClose}
      title=""
      ariaLabel={t(CREDIT_REWARD_MODAL_ARIA_LABEL)}
      maxWidth="sm"
      fullWidth
      customComponentTitle={<CustomTitle />}
      titleContent={false}
      hasTitle={true}
    >
      {isLoading ? (
        <LoadingState />
      ) : (
        <FormContent
          control={control}
          errors={errors}
          text={text}
          selectedAccount={selectedAccount}
          isLoading={isLoading}
          isGeneratingText={isGeneratingText}
          hasError={hasError}
          onChangeLinkedInAccount={onChangeLinkedInAccount}
          onSubmit={onSubmit}
          onClose={onClose}
          onGenerateText={onGenerateArticleText}
        />
      )}
    </ModalBasicLayout>
  );
};

export default CreditRewardModal;
